import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, UserPlus, Sun, Moon, AlertCircle, CheckCircle, Shield } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import TermsModal from './TermsModal';

const Register = ({ onToggle }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  
  // Get referral code from URL
  const referralCode = searchParams.get('ref');
  
  // Debug: Log referral code
  useEffect(() => {
    if (referralCode) {
      console.log('🔗 Referral code detected in URL:', referralCode);
    } else {
      console.log('⚠️ No referral code found in URL');
    }
  }, [referralCode]);

  // Form state (must be before effects that reference it)
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    phone: '',
    country: '',
    password: '',
    confirmPassword: '',
    referralCode: ''
  });

  // List of countries for dropdown (Saudi Arabia first, others alphabetical, Other last)
  const countries = (() => {
    const base = [
      'Saudi Arabia', 'United Arab Emirates', 'Kuwait', 'Qatar', 'Bahrain', 'Oman',
      'United States', 'Canada', 'United Kingdom', 'Germany', 'France', 'Italy',
      'Spain', 'Netherlands', 'Belgium', 'Switzerland', 'Austria', 'Sweden',
      'Norway', 'Denmark', 'Finland', 'Poland', 'Czech Republic', 'Hungary',
      'Portugal', 'Greece', 'Turkey', 'Russia', 'Ukraine', 'Romania',
      'Bulgaria', 'Croatia', 'Slovenia', 'Slovakia', 'Lithuania', 'Latvia',
      'Estonia', 'Ireland', 'Iceland', 'Luxembourg', 'Malta', 'Cyprus',
      'Australia', 'New Zealand', 'Japan', 'South Korea', 'China', 'India',
      'Singapore', 'Malaysia', 'Thailand', 'Philippines', 'Indonesia', 'Vietnam',
      'Brazil', 'Argentina', 'Chile', 'Mexico', 'Colombia', 'Peru',
      'South Africa', 'Egypt', 'Morocco', 'Nigeria', 'Kenya', 'Ghana',
      'Palestine', 'Jordan', 'Lebanon', 'Syria', 'Iraq', 'Iran',
      'Pakistan', 'Bangladesh', 'Sri Lanka', 'Nepal', 'Myanmar', 'Cambodia',
      'Other'
    ];
    const withoutPinned = base.filter(c => c !== 'Saudi Arabia' && c !== 'Other');
    withoutPinned.sort((a, b) => a.localeCompare(b));
    return ['Saudi Arabia', ...withoutPinned, 'Other'];
  })();

  // Country combobox state
  const [countryQuery, setCountryQuery] = useState('');
  const [showCountryList, setShowCountryList] = useState(false);
  const countryBoxRef = useRef(null);

  const filteredCountries = countries.filter(c =>
    c.toLowerCase().includes((countryQuery || '').toLowerCase())
  );

  useEffect(() => {
    // Keep query in sync when formData.country is programmatically set
    setCountryQuery(formData.country || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.country]);

  // Initialize referral code from URL parameter
  useEffect(() => {
    if (referralCode && !formData.referralCode) {
      setFormData(prev => ({
        ...prev,
        referralCode: referralCode
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralCode]); // Only run when URL referralCode changes

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (countryBoxRef.current && !countryBoxRef.current.contains(e.target)) {
        setShowCountryList(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Password strength calculation
  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  // Validation functions
  const validateUsername = (username) => {
    if (!username) return 'Username is required';
    if (username.length < 3) return 'Username must be at least 3 characters';
    if (username.length > 20) return 'Username must be less than 20 characters';
    if (!/^[a-zA-Z0-9_]+$/.test(username)) return 'Username can only contain letters, numbers, and underscores';
    return null;
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return 'Email is required';
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return null;
  };

  const validatePhone = (phone) => {
    if (!phone) return 'Phone number is required';
    // Allow various phone number formats: +1234567890, 0569332910, 123-456-7890, (123) 456-7890, etc.
    const phoneRegex = /^[\+]?[0-9][\d]{0,15}$/;
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
    if (!phoneRegex.test(cleanPhone)) return 'Please enter a valid phone number';
    if (cleanPhone.length < 10) return 'Phone number must be at least 10 digits';
    if (cleanPhone.length > 15) return 'Phone number must be less than 16 digits';
    return null;
  };

  const validateCountry = (country) => {
    if (!country) return 'Country is required';
    return null;
  };

  const validatePassword = (password) => {
    if (!password) return 'Password is required';
    if (password.length < 6) return 'Password must be at least 6 characters';
    return null;
  };

  const validateConfirmPassword = (confirmPassword, password) => {
    if (!confirmPassword) return 'Please confirm your password';
    if (confirmPassword !== password) return 'Passwords do not match';
    return null;
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'username':
        return validateUsername(value);
      case 'email':
        return validateEmail(value);
      case 'phone':
        return validatePhone(value);
      case 'country':
        return validateCountry(value);
      case 'password':
        return validatePassword(value);
      case 'confirmPassword':
        return validateConfirmPassword(value, formData.password);
      default:
        return null;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Update password strength
    if (name === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }

    // Real-time validation
    const error = validateField(name, value);
    setValidationErrors(prev => ({
      ...prev,
      [name]: error
    }));

    // Re-validate confirm password if password changes
    if (name === 'password' && touched.confirmPassword) {
      const confirmError = validateConfirmPassword(formData.confirmPassword, value);
      setValidationErrors(prev => ({
        ...prev,
        confirmPassword: confirmError
      }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Mark all fields as touched
    setTouched({ username: true, email: true, password: true, confirmPassword: true });

    // Validate all fields
    const usernameError = validateUsername(formData.username);
    const emailError = validateEmail(formData.email);
    const passwordError = validatePassword(formData.password);
    const confirmPasswordError = validateConfirmPassword(formData.confirmPassword, formData.password);
    
    if (usernameError || emailError || passwordError || confirmPasswordError) {
      setValidationErrors({
        username: usernameError,
        email: emailError,
        password: passwordError,
        confirmPassword: confirmPasswordError
      });
      setLoading(false);
      return;
    }

    if (!agreedToTerms) {
      setError(t('terms.mustAgree'));
      setLoading(false);
      return;
    }

    try {
      // Use formData.referralCode if provided, otherwise fallback to URL param
      // Convert to uppercase for case-insensitive handling
      const rawReferralCode = formData.referralCode || referralCode || null;
      const finalReferralCode = rawReferralCode ? rawReferralCode.trim().toUpperCase() : null;
      
      // Validate referral code if one is provided
      if (finalReferralCode && finalReferralCode !== '') {
        console.log('🔍 Register: Validating referral code:', finalReferralCode);
        const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
        const validateResponse = await fetch(`${API_BASE_URL}/referrals/validate/${encodeURIComponent(finalReferralCode)}`, {
          method: 'GET',
        });
        
        const validateData = await validateResponse.json();
        
        if (!validateData.valid) {
          console.log('❌ Register: Invalid referral code:', validateData.message);
          setError(validateData.message || 'Invalid referral code. Please check and try again.');
          setLoading(false);
          return;
        }
        console.log('✅ Register: Referral code is valid');
      }
      
      console.log('📝 Register: Starting registration process');
      console.log('🔗 Register: Referral code from form:', formData.referralCode);
      console.log('🔗 Register: Referral code from URL:', referralCode);
      console.log('🔗 Register: Final referral code (uppercase):', finalReferralCode);
      console.log('📝 Register: Form data:', { username: formData.username, email: formData.email });
      const result = await register(formData.username, formData.email, formData.phone, formData.country, formData.password, finalReferralCode);
      console.log('✅ Register: Registration result:', result);
      
      if (result.success) {
        if (result.requiresVerification) {
          console.log('Register: Redirecting to verification pending page');
          // Navigate to verification pending page with user data
          navigate('/verification-pending', { 
            state: { 
              user: result.user, 
              message: result.message 
            } 
          });
        } else {
          // Fallback for cases where verification is not required
          console.log('Register: Registration successful, redirecting to dashboard');
          navigate('/dashboard');
        }
      } else {
        console.log('Register: Registration failed:', result.error);
        setError(result.error);
      }
    } catch (err) {
      console.error('Register: Registration error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = !validationErrors.username && !validationErrors.email &&
                     !validationErrors.password && !validationErrors.confirmPassword &&
                     formData.username && formData.email && formData.password && formData.confirmPassword &&
                     agreedToTerms;

  const getPasswordStrengthText = (strength) => {
    switch (strength) {
      case 0:
      case 1: return 'Very Weak';
      case 2: return 'Weak';
      case 3: return 'Fair';
      case 4: return 'Good';
      case 5: return 'Strong';
      default: return '';
    }
  };

  const getPasswordStrengthColor = (strength) => {
    switch (strength) {
      case 0:
      case 1: return 'bg-red-500';
      case 2: return 'bg-orange-500';
      case 3: return 'bg-yellow-500';
      case 4: return 'bg-blue-500';
      case 5: return 'bg-green-500';
      default: return 'bg-gray-300';
    }
  };

  return (
    <div className={`min-h-screen relative overflow-hidden ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob" style={{ backgroundColor: 'var(--chart-1)' }}></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000" style={{ backgroundColor: 'var(--chart-2)' }}></div>
        <div className="absolute top-40 left-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000" style={{ backgroundColor: 'var(--chart-3)' }}></div>
      </div>


      <div className="relative z-10 min-h-screen flex items-center justify-center p-3">
        <div className="max-w-2xl w-full">
          {/* Logo above the form */}
          <div className="text-center -mt-24">
            <img 
              src={isDark ? "/images/Logo-dark.png" : "/images/Logo-light.png"} 
              alt="Logo"
              className="w-48 h-48 object-contain mx-auto"
            />
          </div>
          <div className="glass-card">
            {/* Header */}
            <div className={`text-center mb-4 ${i18n.language === 'ar' ? 'font-arabic' : ''}`}>
              <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>
                {t('auth.createAccount')}
              </h1>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                {t('auth.createAccountDescription')}
              </p>
            </div>

            {/* Error message display */}
            {error && (
              <div className={`mb-4 p-3 rounded-md flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`} style={{ backgroundColor: 'var(--error-background)', border: '1px solid var(--error-color)' }}>
                <AlertCircle className="h-5 w-5 flex-shrink-0" style={{ color: 'var(--error-color)' }} />
                <p className="text-sm" style={{ color: 'var(--error-color)' }}>
                  {error}
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className={`space-y-4 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Username field */}
                <div>
                  <label htmlFor="username" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                    {t('auth.username')}
                  </label>
                  <div className="relative">
                    <input
                      id="username"
                      name="username"
                      type="text"
                      required
                      value={formData.username}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`glass-input ${i18n.language === 'ar' ? 'pl-10 text-right' : 'pr-10 text-left'} ${touched.username && validationErrors.username ? 'border-red-400' : ''}`}
                      dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                      placeholder={t('auth.usernamePlaceholder')}
                      aria-describedby={touched.username && validationErrors.username ? 'username-error' : undefined}
                      aria-invalid={touched.username && validationErrors.username ? 'true' : 'false'}
                    />
                    {touched.username && !validationErrors.username && formData.username && (
                      <CheckCircle className={`absolute top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-500 ${i18n.language === 'ar' ? 'left-3' : 'right-3'}`} />
                    )}
                  </div>
                  {touched.username && validationErrors.username && (
                    <p id="username-error" className={`mt-1 text-sm flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-1' : 'space-x-1'}`} style={{ color: 'var(--error-color)' }}>
                      <AlertCircle className="h-4 w-4" />
                      <span>{validationErrors.username}</span>
                    </p>
                  )}
                </div>

                {/* Email field */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                    {t('auth.email')}
                  </label>
                  <div className="relative">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`glass-input ${i18n.language === 'ar' ? 'pl-10 text-right' : 'pr-10 text-left'} ${touched.email && validationErrors.email ? 'border-red-400' : ''}`}
                      dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                      placeholder={t('auth.emailPlaceholder')}
                      aria-describedby={touched.email && validationErrors.email ? 'email-error' : undefined}
                      aria-invalid={touched.email && validationErrors.email ? 'true' : 'false'}
                    />
                    {touched.email && !validationErrors.email && formData.email && (
                      <CheckCircle className={`absolute top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-500 ${i18n.language === 'ar' ? 'left-3' : 'right-3'}`} />
                    )}
                  </div>
                  {touched.email && validationErrors.email && (
                    <p id="email-error" className={`mt-1 text-sm flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-1' : 'space-x-1'}`} style={{ color: 'var(--error-color)' }}>
                      <AlertCircle className="h-4 w-4" />
                      <span>{validationErrors.email}</span>
                    </p>
                  )}
                </div>

                {/* Phone field */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                    {t('auth.phoneNumber')}
                  </label>
                  <div className="relative">
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`glass-input ${i18n.language === 'ar' ? 'pl-10 text-right' : 'pr-10 text-left'} ${touched.phone && validationErrors.phone ? 'border-red-400' : ''}`}
                      dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                      placeholder={t('auth.phoneNumberPlaceholder')}
                      aria-describedby={touched.phone && validationErrors.phone ? 'phone-error' : undefined}
                      aria-invalid={touched.phone && validationErrors.phone ? 'true' : 'false'}
                    />
                    {touched.phone && !validationErrors.phone && formData.phone && (
                      <CheckCircle className={`absolute top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-500 ${i18n.language === 'ar' ? 'left-3' : 'right-3'}`} />
                    )}
                  </div>
                  {touched.phone && validationErrors.phone && (
                    <p id="phone-error" className={`mt-1 text-sm flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-1' : 'space-x-1'}`} style={{ color: 'var(--error-color)' }}>
                      <AlertCircle className="h-4 w-4" />
                      <span>{validationErrors.phone}</span>
                    </p>
                  )}
                </div>

                {/* Country field (searchable combobox) */}
                <div>
                  <label htmlFor="country" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                    {t('auth.country')}
                  </label>
                  <div className="relative" ref={countryBoxRef}>
                    <input
                      id="country"
                      name="country"
                      type="text"
                      autoComplete="off"
                      required
                      value={countryQuery}
                      onChange={(e) => {
                        setCountryQuery(e.target.value);
                        handleChange({ target: { name: 'country', value: e.target.value } });
                        if (!showCountryList) setShowCountryList(true);
                      }}
                      onFocus={() => setShowCountryList(true)}
                      onBlur={handleBlur}
                      className={`glass-input text-sm ${i18n.language === 'ar' ? 'pl-10 text-right' : 'pr-10 text-left'} ${touched.country && validationErrors.country ? 'border-red-400' : ''}`}
                      dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                      placeholder={t('auth.countryPlaceholder')}
                      aria-expanded={showCountryList}
                      aria-controls="country-options"
                      aria-describedby={touched.country && validationErrors.country ? 'country-error' : undefined}
                      aria-invalid={touched.country && validationErrors.country ? 'true' : 'false'}
                    />
                    {touched.country && !validationErrors.country && formData.country && (
                      <CheckCircle className={`absolute top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-500 pointer-events-none ${i18n.language === 'ar' ? 'left-3' : 'right-3'}`} />
                    )}

                    {showCountryList && (
                      <div
                        id="country-options"
                        className="absolute z-20 mt-1 w-full max-h-52 overflow-auto rounded-md shadow-lg bg-card border border-border ring-1 ring-black ring-opacity-5 focus:outline-none"
                        role="listbox"
                      >
                        {(filteredCountries.length > 0 ? filteredCountries : ['No results']).map((country) => (
                          country === 'No results' ? (
                            <div
                              key="no-results"
                              className="px-3 py-2 text-sm text-muted-foreground"
                            >
                              No results
                            </div>
                          ) : (
                            <div
                              key={country}
                              role="option"
                              aria-selected={formData.country === country}
                              className={`px-3 py-2 cursor-pointer text-sm text-card-foreground ${formData.country === country ? 'bg-muted' : 'hover:bg-muted/50'}`}
                              onMouseDown={(e) => {
                                // onMouseDown to select before input loses focus
                                e.preventDefault();
                                handleChange({ target: { name: 'country', value: country } });
                                setCountryQuery(country);
                                setShowCountryList(false);
                              }}
                            >
                              {country}
                            </div>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                  {touched.country && validationErrors.country && (
                    <p id="country-error" className={`mt-1 text-sm flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-1' : 'space-x-1'}`} style={{ color: 'var(--error-color)' }}>
                      <AlertCircle className="h-4 w-4" />
                      <span>{validationErrors.country}</span>
                    </p>
                  )}
                </div>

                {/* Password field */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                    {t('auth.password')}
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={formData.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`glass-input ${i18n.language === 'ar' ? 'pl-20 text-right' : 'pr-20 text-left'} ${touched.password && validationErrors.password ? 'border-red-400' : ''}`}
                      dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                      placeholder={t('auth.passwordPlaceholder')}
                      aria-describedby={touched.password && validationErrors.password ? 'password-error' : undefined}
                      aria-invalid={touched.password && validationErrors.password ? 'true' : 'false'}
                    />
                    <div className={`absolute top-1/2 transform -translate-y-1/2 flex items-center ${i18n.language === 'ar' ? 'left-3 space-x-reverse space-x-2' : 'right-3 space-x-2'}`}>
                      {touched.password && !validationErrors.password && formData.password && (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      )}
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                  
                  {/* Password strength indicator */}
                  {formData.password && (
                    <div className="mt-1">
                      <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'} mb-1`}>
                        <Shield className="h-3 w-3" style={{ color: 'var(--text-muted)' }} />
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                          Password strength: {getPasswordStrengthText(passwordStrength)}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div 
                          className={`h-1.5 rounded-full transition-all duration-300 ${getPasswordStrengthColor(passwordStrength)}`}
                          style={{ width: `${(passwordStrength / 5) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                  
                  {touched.password && validationErrors.password && (
                    <p id="password-error" className={`mt-1 text-sm flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-1' : 'space-x-1'}`} style={{ color: 'var(--error-color)' }}>
                      <AlertCircle className="h-4 w-4" />
                      <span>{validationErrors.password}</span>
                    </p>
                  )}
                </div>

                {/* Confirm Password field */}
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                    {t('auth.confirmPassword')}
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      required
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`glass-input ${i18n.language === 'ar' ? 'pl-20 text-right' : 'pr-20 text-left'} ${touched.confirmPassword && validationErrors.confirmPassword ? 'border-red-400' : ''}`}
                      dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                      placeholder={t('auth.confirmPasswordPlaceholder')}
                      aria-describedby={touched.confirmPassword && validationErrors.confirmPassword ? 'confirm-password-error' : undefined}
                      aria-invalid={touched.confirmPassword && validationErrors.confirmPassword ? 'true' : 'false'}
                    />
                    <div className={`absolute top-1/2 transform -translate-y-1/2 flex items-center ${i18n.language === 'ar' ? 'left-3 space-x-reverse space-x-2' : 'right-3 space-x-2'}`}>
                      {touched.confirmPassword && !validationErrors.confirmPassword && formData.confirmPassword && (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      )}
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                  {touched.confirmPassword && validationErrors.confirmPassword && (
                    <p id="confirm-password-error" className={`mt-1 text-sm flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-1' : 'space-x-1'}`} style={{ color: 'var(--error-color)' }}>
                      <AlertCircle className="h-4 w-4" />
                      <span>{validationErrors.confirmPassword}</span>
                    </p>
                  )}
                </div>
              </div>

              {/* Referral Code field (optional) */}
              <div>
                <label htmlFor="referralCode" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                  {t('auth.referralCode')} <span className="text-xs" style={{ color: 'var(--text-muted)' }}>({t('auth.optional')})</span>
                </label>
                <div className="relative">
                  <input
                    id="referralCode"
                    name="referralCode"
                    type="text"
                    value={formData.referralCode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`glass-input ${i18n.language === 'ar' ? 'pl-10 text-right' : 'pr-10 text-left'}`}
                    dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                    placeholder={t('auth.referralCodePlaceholder') || 'Enter referral code (optional)'}
                  />
                  {formData.referralCode && (
                    <CheckCircle className={`absolute top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-500 ${i18n.language === 'ar' ? 'left-3' : 'right-3'}`} />
                  )}
                </div>
                {formData.referralCode && (
                  <p className={`mt-1 text-xs flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-1' : 'space-x-1'}`} style={{ color: 'var(--text-muted)' }}>
                    {t('auth.referralCodeHint') || 'Referral code will be applied to your account'}
                  </p>
                )}
              </div>

              {/* Terms & Conditions checkbox */}
              <div
                className={`flex items-start gap-2 ${i18n.language === 'ar' ? 'justify-start text-right' : 'text-left'}`}
                dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
              >
                <input
                  id="agreeToTerms"
                  name="agreeToTerms"
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded cursor-pointer"
                  style={{ accentColor: 'var(--accent-primary)' }}
                  aria-describedby="terms-agreement-text"
                />
                <label
                  htmlFor="agreeToTerms"
                  id="terms-agreement-text"
                  className="text-sm cursor-pointer select-none"
                  dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                  style={{ color: 'var(--text-secondary)', textAlign: i18n.language === 'ar' ? 'right' : 'left' }}
                >
                  {t('terms.agreeLabel')}{' '}
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    className="font-medium hover:underline transition-colors"
                    style={{ color: 'var(--accent-primary)' }}
                  >
                    {t('terms.linkText')}
                  </button>
                </label>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={loading || !isFormValid}
                className="glass-button w-full py-3 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                aria-describedby={loading ? 'loading-message' : undefined}
              >
                {loading ? (
                  <div className={`flex items-center justify-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Creating account...</span>
                  </div>
                ) : (
                  t('auth.createAccount')
                )}
              </button>

              {/* Loading message for screen readers */}
              {loading && (
                <div id="loading-message" className="sr-only">
                  Please wait while we create your account
                </div>
              )}
            </form>

            {/* Footer */}
            <div className={`mt-4 text-center ${i18n.language === 'ar' ? 'font-arabic' : ''}`}>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                {i18n.language === 'ar' ? (
                  <>
                    <button
                      onClick={onToggle}
                      className="font-medium hover:underline transition-colors"
                      style={{ color: 'var(--accent-primary)' }}
                    >
                      {t('auth.signIn')}
                    </button>
                    {' '}{t('auth.haveAccount')}
                  </>
                ) : (
                  <>
                    {t('auth.haveAccount')}{' '}
                    <button
                      onClick={onToggle}
                      className="font-medium hover:underline transition-colors"
                      style={{ color: 'var(--accent-primary)' }}
                    >
                      {t('auth.signIn')}
                    </button>
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {showTermsModal && (
        <TermsModal
          titleKey="terms.userTitle"
          bodyKey="terms.userBody"
          onClose={() => setShowTermsModal(false)}
        />
      )}
    </div>
  );
};

export default Register; 